import React, {useEffect, useState} from 'react';
import ModalWidget from "../ModalWidget";
import {ReactComponent as ImportSvg} from "../../../assets/svg/import.svg";
import {ReactComponent as PenSvg} from "../../../assets/svg/pen.svg";
import {ReactComponent as PlusSvg} from "../../../assets/svg/plus.svg";
import {AccountType} from "../../../models/AccountType";
import {capitalizeFirstLetter} from "../../../services/HelperService";
import {Button} from "react-bootstrap";
import {AddUserType} from "../../../models/AddUserType";
import UserModal from "./UserModal";
import ImportWizard from "./ImportWizard";
import AssignStudent from "./AssignStudent";
import {getUsers} from "../../../services/UserService";

interface AddUserModalProps {
    userType?: string;
    show?: boolean;
    onHide?: () => void;
    classroomId?: string;
    classroomName?: string;
    termId?: string;
    onSuccess?: () => void;
}

function AddUserModal({show = true, userType = '', onHide, classroomId, classroomName, termId, onSuccess}: AddUserModalProps) {
    const capitalWord = capitalizeFirstLetter(userType)
    const [addUserType, setAddUserType] = useState<string | null>(null)
    const [hasExistingStudents, setHasExistingStudents] = useState<boolean | null>(null)

    const showAddExisting = userType === AccountType.STUDENT && !!classroomId
    const addExistingDisabled = hasExistingStudents !== true

    useEffect(() => {
        if (!showAddExisting || !show) return
        let cancelled = false
        getUsers(AccountType.STUDENT, { limit: 1 })
            .then(res => { if (!cancelled) setHasExistingStudents((res.items?.length ?? 0) > 0) })
            .catch(() => { if (!cancelled) setHasExistingStudents(false) })
        return () => { cancelled = true }
    }, [showAddExisting, show])

    const closeAll = () => { setAddUserType(null); onHide?.(); };

    return (
        <>
            <ModalWidget
                headerIcon={new AccountType(userType as any).getBorderLogoString() ?? undefined}
                show={show && !addUserType}
                title={'Add ' + capitalWord + 's'}
                description={'How do you want to add ' + userType + 's?'}
                hasFooter={false}
                navigateBack={!onHide}
                onHide={onHide}
            >
                <div className='d-flex justify-content-between flex-wrap gap-2'>
                    <Button variant="outline-dark"
                            className='d-flex align-items-center font-bold'
                            onClick={()=>setAddUserType(AddUserType.IMPORT)}>
                        Import File
                        <div className='mx-1'/>
                        <ImportSvg/>
                    </Button>

                    <Button variant="outline-dark"
                            className='d-flex align-items-center font-bold'
                            onClick={()=>setAddUserType(AddUserType.MANUAL)}>
                        Add Manually
                        <div className='mx-1'/>
                        <PenSvg/>
                    </Button>

                    {showAddExisting && (
                        <Button variant="outline-dark"
                                className='d-flex align-items-center font-bold'
                                disabled={addExistingDisabled}
                                title={addExistingDisabled ? 'No existing students yet — add one first' : undefined}
                                onClick={()=>setAddUserType(AddUserType.ADD_EXISTING)}>
                            Add Existing
                            <div className='mx-1'/>
                            <PlusSvg/>
                        </Button>
                    )}
                </div>

            </ModalWidget>

            {/*MANUAL add user types STARTS*/}
            <UserModal show={addUserType === AddUserType.MANUAL && userType === AccountType.TEACHER}
                       onHide={()=>setAddUserType(null)}
                       userType={AccountType.TEACHER}/>
            <UserModal show={addUserType === AddUserType.MANUAL && userType === AccountType.STUDENT}
                       onHide={()=>setAddUserType(null)}
                       userType={AccountType.STUDENT}
                       attachedUserType={AccountType.GUARDIAN}
                       classroomId={classroomId}
                       classroomName={classroomName}
                       termId={termId}
                       onSuccess={onSuccess}/>
            <UserModal show={addUserType === AddUserType.MANUAL && userType === AccountType.GUARDIAN}
                       onHide={()=>setAddUserType(null)}
                       userType={AccountType.GUARDIAN}
                       attachedUserType={AccountType.STUDENT}/>
            {/*MANUAL add user type ENDS HEREs*/}

            {/*IMPORT add user type STARTS*/}
            <ImportWizard show={addUserType === AddUserType.IMPORT}
                          userType={userType}
                          onHide={()=>setAddUserType(null)}
                          onClose={closeAll}
                          classroomName={classroomName}
                          classroomId={classroomId}
                          termId={termId}
                          onSuccess={onSuccess}/>
            {/*IMPORT add user type ENDS HERE*/}

            {/*ADD EXISTING student type STARTS*/}
            {showAddExisting && (
                <AssignStudent show={addUserType === AddUserType.ADD_EXISTING}
                               classroomId={classroomId}
                               termId={termId}
                               onHide={()=>setAddUserType(null)}
                               onSuccess={() => { onSuccess?.(); closeAll(); }}/>
            )}
            {/*ADD EXISTING student type ENDS HERE*/}
        </>

    );
}

export default AddUserModal;
